import React, { useEffect, useMemo, useState } from 'react';
import Type from 'prop-types';
import { capitalize } from 'lodash';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import Div100vh from 'react-div-100vh';
import { withSideNavMenu } from 'hocs';
import { authStore, permissionsStore, userStore } from 'stores';
import { useLocation } from 'react-router-dom';
import BottomNavMenu from '../BottomNavMenu/BottomNavMenu';
import { MENU, tabs } from './BottomMenu.mock';
import { JuncerFinanceIcon } from './JuncerFinanceIcon';
import { MonthlyPlansWrapper } from './monthly-plans-timer';
import styles from './Page.module.css';

const Page = observer(
  ({
    fullScreen,
    backgroundColor,
    children,
    className,
    toggleSideMenu,
    bottomNavMenuRef,
    ...other
  }) => {
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('');
    const PageComponent = fullScreen ? Div100vh : 'div';
    const colorName = `color${capitalize(backgroundColor)}`;
    const { isLoggedIn } = authStore;
    const {
      hasFinanceAccess,
      isCanViewJuncerFinances,
      canViewPromisesPage,
    } = permissionsStore;

    const user = userStore;
    const badgeValue = user.user.promiseTask;

    permissionsStore.initialize();

    const filteredTabs = useMemo(() => {
      return tabs.filter((tab) => {
        if (tab.value === '/finance') {
          return hasFinanceAccess;
        }
        if (tab.value === '/deliveries') {
          return !hasFinanceAccess;
        }
        return true;
      });
    }, [hasFinanceAccess]);

    const preparedTabs = useMemo(
      () =>
        filteredTabs.map((tab) => {
          if (tab.id !== 3) return tab;
          if (isCanViewJuncerFinances) {
            return {
              ...tab,
              value: '/juncer_finances',
              icon: JuncerFinanceIcon,
              dataTestId: undefined,
            };
          }
          const preparedTab = { ...tab, badge: badgeValue };
          if (canViewPromisesPage) {
            preparedTab.value = '/promises';
          }
          return preparedTab;
        }),
      [
        filteredTabs,
        badgeValue,
        canViewPromisesPage,
        isCanViewJuncerFinances,
      ],
    );

    useEffect(() => {
      const currentTab = preparedTabs.find((tab) => {
        if (tab.type !== 'link') return false;
        const tabPathname = tab.value.split('?')[0];
        return (
          location.pathname === tabPathname ||
          location.pathname.startsWith(`${tabPathname}/`)
        );
      });
      setActiveTab(currentTab ? currentTab.value : '');
    }, [location.pathname, preparedTabs]);

    const handleActionItemSelected = (item) => {
      if (item === MENU) {
        toggleSideMenu();
      }
    };

    return (
      <PageComponent
        style={{ minHeight: fullScreen ? '100rvh' : null }}
        className={cn(
          styles.page,
          {
            [styles[colorName]]: true,
          },
          className,
        )}
        {...other}
      >
        {children}
        {isLoggedIn && <MonthlyPlansWrapper />}
        {isLoggedIn && permissionsStore.isInitialized && (
          <BottomNavMenu
            activeTab={activeTab}
            onChange={handleActionItemSelected}
            tabs={preparedTabs}
            ref={bottomNavMenuRef}
          />
        )}
      </PageComponent>
    );
  },
);

Page.propTypes = {
  fullScreen: Type.bool,
  backgroundColor: Type.oneOf(['primary', 'secondary']),
  className: Type.string,
  component: Type.elementType,
  children: Type.node.isRequired,
  bottomNavMenuRef: Type.oneOfType([
    Type.func,
    // eslint-disable-next-line react/forbid-prop-types
    Type.shape({ current: Type.any }),
  ]),
  toggleSideMenu: Type.func.isRequired,
};

Page.defaultProps = {
  backgroundColor: 'primary',
  className: null,
  component: 'div',
  fullScreen: false,
  bottomNavMenuRef: null,
};

const PageWithSideNavMenu = withSideNavMenu(Page);
export default PageWithSideNavMenu;
